export interface ParsedTransaction {
	ticker: string
	exchange: string
	type: "BUY" | "SELL" | "DEPOSIT" | "WITHDRAWAL"
	shares: number
	price: number
	date: string
}

// CSV format:
//   ticker,exchange,type,shares,price,date
//
// For stock transactions (BUY/SELL):
//   All fields are required. Exchange should match the code shown in search results
//   (e.g. NASDAQ, NYSE, LSE). Shares and price must be positive numbers.
//
// For cash transactions (DEPOSIT/WITHDRAWAL):
//   ticker and exchange may be empty. shares = amount, price = 1 (ignored on import).
//   Example row: ,,DEPOSIT,1000,1,2024-01-15

export const validateCSV = (text: string): ParsedTransaction[] => {
	const lines = text.split(/\r?\n/).filter((line) => line.trim() !== "")
	if (lines.length < 2) {
		throw new Error("CSV file is empty or missing data rows.")
	}

	const headers = lines[0]
		.toLowerCase()
		.split(",")
		.map((h) => h.trim())
	const requiredHeaders = ["ticker", "exchange", "type", "shares", "price", "date"]

	for (const req of requiredHeaders) {
		if (!headers.includes(req)) {
			throw new Error(`Missing required column: ${req}`)
		}
	}

	const tickerIdx = headers.indexOf("ticker")
	const exchangeIdx = headers.indexOf("exchange")
	const typeIdx = headers.indexOf("type")
	const sharesIdx = headers.indexOf("shares")
	const priceIdx = headers.indexOf("price")
	const dateIdx = headers.indexOf("date")

	const transactions: ParsedTransaction[] = []

	for (let i = 1; i < lines.length; i++) {
		const values = lines[i].split(",").map((v) => v.trim())
		if (values.length < requiredHeaders.length) continue

		const ticker = values[tickerIdx]
		const exchange = values[exchangeIdx].toUpperCase()
		const type = values[typeIdx].toUpperCase()
		const shares = parseFloat(values[sharesIdx])
		const price = parseFloat(values[priceIdx])
		const dateStr = values[dateIdx]

		const isCashRow = type === "DEPOSIT" || type === "WITHDRAWAL"

		// Validations
		if (!isCashRow && !ticker) throw new Error(`Row ${i + 1}: Ticker is required`)
		if (type !== "BUY" && type !== "SELL" && type !== "DEPOSIT" && type !== "WITHDRAWAL") {
			throw new Error(`Row ${i + 1}: Type must be BUY, SELL, DEPOSIT, or WITHDRAWAL`)
		}
		if (isNaN(shares) || shares <= 0) throw new Error(`Row ${i + 1}: Shares/amount must be a positive number`)
		if (!isCashRow && (isNaN(price) || price <= 0)) throw new Error(`Row ${i + 1}: Price must be a positive number`)

		// Date validation (simple YYYY-MM-DD check)
		if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
			throw new Error(`Row ${i + 1}: Date must be in YYYY-MM-DD format`)
		}

		if (isCashRow) {
			// Cash row: treat shares column as amount, price = 1
			transactions.push({
				ticker: ticker || "",
				exchange: exchange || "",
				type: type as "DEPOSIT" | "WITHDRAWAL",
				shares,
				price: 1,
				date: new Date(dateStr).toISOString(),
			})
		} else {
			transactions.push({
				ticker,
				exchange,
				type: type as "BUY" | "SELL",
				shares,
				price,
				date: new Date(dateStr).toISOString(),
			})
		}
	}

	return transactions
}
